import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { MessengerModal } from "@/components/landing/LandingModals";
import {
  Corner,
  LandingFooter,
  LandingHeader,
  SectionTag,
  useMessengerLinks,
} from "@/components/landing/LandingChrome";


const steps = [
  {
    num: "01",
    title: "Send your request",
    text: "Share your shipment details once — lane, container type and ready date.",
  },
  {
    num: "02",
    title: "Findrates does the chasing",
    text: "AI sends the requests to your own NVOCC network, follows up automatically, and pulls in verified lines when you need extra coverage.",
  },

  {
    num: "03",
    title: "Compare and agree",
    text: "Review the offers side by side, then negotiate and confirm terms in one place — ready to turn into a pro forma invoice.",
  },
  {
    num: "04",
    title: "Pay and ship",
    text: "Settle quickly through our global payment partner and move your cargo.",
  },
];

const quotes = [
  { label: "Quote 01", price: "$1,840 · Maersk", meta: "19 days transit · Valid 7 days" },
  { label: "Quote 02", price: "$1,920 · CMA CGM", meta: "21 days transit · Valid 5 days" },
];

const stats = [
  { value: "Up to 3× faster", label: "Than chasing rates by hand" },
  { value: "6 hours", label: "From request to 2 verified quotes" },
  { value: "$0", label: "To set up — no upfront fees" },
];


const ShipAnimation = () => (
  <div
    className="relative ml-auto hidden h-32 w-full max-w-[260px] overflow-hidden border-2 md:block"
    style={{ borderColor: "hsl(var(--accent))" }}
    aria-hidden="true"
  >
    <div className="ship-sail absolute bottom-6 left-0">
      <svg width="120" height="60" viewBox="0 0 120 60" fill="none">
        {/* containers */}
        <rect x="30" y="16" width="16" height="10" fill="hsl(var(--accent))" />
        <rect x="48" y="16" width="16" height="10" fill="hsl(var(--foreground))" />
        <rect x="66" y="16" width="16" height="10" fill="hsl(var(--accent))" />
        <rect x="39" y="6" width="16" height="10" fill="hsl(var(--foreground))" />
        <rect x="57" y="6" width="16" height="10" fill="hsl(var(--accent))" />
        {/* bridge */}
        <rect x="90" y="10" width="12" height="16" fill="hsl(var(--foreground))" />
        {/* hull */}
        <path d="M14 26 H108 L98 42 H26 Z" fill="hsl(var(--foreground))" />
      </svg>
    </div>
  </div>
);


const Landing = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const { wa, tg } = useMessengerLinks();


  return (
    <div className="landing-light min-h-screen bg-background text-foreground">
      <Helmet>
        <title>FindRates — Faster and Better Freight Rates</title>
        <meta
          name="description"
          content="FindRates automates rate requests, follow-ups and tracking across your NVOCC network with AI. Need more options? Two verified quotes in 6 hours."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://findrates.ai/" />
        <meta property="og:title" content="FindRates — Faster and Better Freight Rates" />
        <meta property="og:description" content="Automate rate requests, follow-ups and tracking across your NVOCC network with AI. Need more options? Two verified quotes in 6 hours." />
        <meta property="og:url" content="https://findrates.ai/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="FindRates — Faster and Better Freight Rates" />
        <meta name="twitter:description" content="Automate rate requests, follow-ups and tracking across your NVOCC network with AI. Two verified quotes in 6 hours." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "FindRates",
            url: "https://findrates.ai/",
            description:
              "AI-powered freight rate sourcing and NVOCC network automation for freight forwarders.",
          })}
        </script>
      </Helmet>

      <MessengerModal open={chatOpen} onClose={() => setChatOpen(false)} />

      <LandingHeader
        wa={wa}
        tg={tg}
        showDemo={false}
        onChat={() => setChatOpen(true)}
      />



      <main>
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-border">
          <div className="tech-grid pointer-events-none absolute inset-0" aria-hidden="true" />
          <div
            className="tech-dots pointer-events-none absolute inset-x-0 bottom-0 h-40 opacity-60"
            aria-hidden="true"
          />
          <div className="relative mx-auto max-w-7xl px-6 py-24 md:py-32">
            <div className="mb-10">
              <SectionTag num="01" label="Intro" />
            </div>
            <div className="grid items-end gap-12 md:grid-cols-[1.4fr_0.6fr]">
              <div>
                <h1 className="max-w-3xl text-4xl font-semibold leading-[0.95] tracking-tight md:text-7xl">
                  Findrates for Faster and Better Freight Rates
                </h1>
                <p className="mt-8 max-w-md text-xs uppercase leading-relaxed tracking-wider text-muted-foreground">
                  <span>AI runs your rate requests and follow-ups.</span>
                  <br />
                  <span>Need more options? Two verified quotes in 6 hours.</span>
                </p>


              </div>

              <ShipAnimation />

            </div>

            {/* Primary CTA bar */}
            <div className="mt-14 grid border border-border">
              <button
                type="button"
                onClick={() => setChatOpen(true)}
                className="border border-accent-strong bg-accent-strong px-6 py-5 text-center text-xs uppercase tracking-widest text-accent-foreground transition-opacity hover:opacity-90"
              >
                Get rates
              </button>
            </div>

            {/* Secondary CTA */}
            <div className="mt-4">
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
              >
                <span>How it works</span>
                <span aria-hidden="true" className="text-accent-strong">↓</span>
              </a>
            </div>


            {/* Stats */}
            <div className="mt-14 grid gap-8 sm:grid-cols-3">
              {stats.map((s) => (
                <div key={s.label} className="relative pl-4 pt-4">
                  <Corner />
                  <div className="text-2xl font-semibold tracking-tight">{s.value}</div>
                  <p className="mt-2 text-xs uppercase leading-relaxed tracking-wider text-muted-foreground">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CRM */}
        <section className="border-b border-border">
          <div className="mx-auto max-w-7xl px-6 py-20 md:py-24">
            <SectionTag num="02" label="FindRates" />
            <div className="mt-8 grid gap-10 md:grid-cols-[1.3fr_0.7fr]">
              <h2 className="max-w-2xl text-3xl font-semibold leading-tight tracking-tight md:text-5xl">
                Service for the NVOCC network you already have
              </h2>
              <p className="text-sm leading-relaxed text-muted-foreground">
                <span>
                  FindRates helps you manage your existing carrier network more effectively
                  and find new, verified rate providers using artificial intelligence,
                  automating rate requests, follow-ups, and tracking — allowing you to
                  respond faster and process more orders without increasing the amount of
                  manual work.
                </span>
              </p>
            </div>

            <div className="mt-12 grid border-l border-t border-border sm:grid-cols-3">
              {[
                "Automated rate requests to your own agents",
                "AI follow-ups until every quote lands",
                "Tracking, terms and payments in one place",
              ].map((item) => (
                <div
                  key={item}
                  className="border-b border-r border-border bg-card p-8 text-xs uppercase leading-relaxed tracking-wider"
                >
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <p className="mt-10 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              <span>
                Rate searches are conducted through the Findrates system: we partner with
                trusted carriers and operators to ensure high-quality transportation — the
                same workflow, the same dashboard.
              </span>
            </p>
          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="border-b border-border">
          <div className="mx-auto max-w-7xl px-6 py-20 md:py-24">
            <SectionTag num="03" label="How it works" />
            <h2 className="mt-8 max-w-3xl text-3xl font-semibold leading-tight tracking-tight md:text-5xl">
              One request in, two verified quotes back in 6 hours.
            </h2>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              <span>
                Our system automatically tracks suppliers. You no longer need to contact
                suppliers by phone, through messaging apps, or by email. You no longer need
                to manually compile the terms and conditions.
              </span>
            </p>


            <div className="mt-14 grid border-l border-t border-border sm:grid-cols-2 md:grid-cols-4">
              {steps.map((s) => (
                <div
                  key={s.num}
                  className="flex min-h-[220px] flex-col border-b border-r border-border bg-card p-8"
                >
                  <span className="text-xs uppercase tracking-widest text-accent-strong">
                    {s.num}
                  </span>
                  <h3 className="mt-6 text-xl font-semibold tracking-tight">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {s.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* From request to quotes */}
        <section className="relative overflow-hidden border-b border-border">
          <div
            className="tech-grid pointer-events-none absolute inset-0 opacity-70"
            aria-hidden="true"
          />
          <div className="relative mx-auto max-w-7xl px-6 py-20 md:py-24">
            <SectionTag num="04" label="Request to quotes" />

            <div className="mt-12 grid items-stretch gap-6 md:grid-cols-[1fr_auto_1fr]">
              <div className="border border-border bg-card p-8">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                  WhatsApp
                </div>
                <div className="mt-8 space-y-2 text-lg tracking-tight">
                  <p>Dubai → Mombasa</p>
                  <p>1 × 40HC</p>
                  <p>Ready 18 Aug</p>
                </div>
              </div>

              <div
                className="flex items-center justify-center text-2xl text-accent"
                aria-hidden="true"
              >
                <span className="hidden md:inline">→</span>
                <span className="md:hidden">↓</span>
              </div>

              <div className="divide-y divide-border border border-border bg-card">
                {quotes.map((q) => (
                  <div key={q.label} className="p-6">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      {q.label}
                    </div>
                    <p className="mt-2 text-lg tracking-tight">{q.price}</p>
                    <p className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">
                      {q.meta}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>



        {/* Built by */}
        <section className="border-b border-border">
          <div className="mx-auto max-w-7xl px-6 py-20 md:py-24">
            <SectionTag num="05" label="Who builds it" />
            <h2 className="mt-8 max-w-3xl text-3xl font-semibold leading-tight tracking-tight md:text-4xl">
              Built by freight veterans and AI engineers, together.
            </h2>
            <p className="mt-6 max-w-2xl text-xs uppercase leading-relaxed tracking-wider text-muted-foreground">
              <span>
                Shaped by input from 55+ freight forwarders across the UAE, India, Africa and
                Europe.
              </span>
            </p>
          </div>
        </section>


        {/* CTA */}
        <section id="get-rates" className="border-b border-border bg-foreground text-background">
          <div className="mx-auto max-w-7xl px-6 py-24">
            <div className="flex items-center text-xs uppercase tracking-widest">
              <span className="bg-accent-strong px-2 py-1 text-accent-foreground">06</span>
              <span className="border border-l-0 border-background/30 px-3 py-1">Get started</span>
            </div>
            <h2 className="mt-8 max-w-3xl text-4xl font-semibold leading-[0.95] tracking-tight md:text-6xl">
              Need a rate? Send one request.
            </h2>
            <div className="mt-12 grid border border-background/30">
              <button
                type="button"
                onClick={() => setChatOpen(true)}
                className="border border-accent-strong bg-accent-strong px-6 py-5 text-center text-xs uppercase tracking-widest text-accent-foreground transition-opacity hover:opacity-90"
              >
                Get rates
              </button>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter wa={wa} tg={tg} />


    </div>
  );
};

export default Landing;
